import type { PreviewResult } from '../../game/score-preview';

interface ScoreBarProps {
  cumulative: number;
  target: number;
  movesUsed: number;
  moveCount: number;
  preview: PreviewResult | null;
}

export function ScoreBar({ cumulative, target, movesUsed, moveCount, preview }: ScoreBarProps) {
  const hasPreview = preview && (preview.words.length > 0 || preview.message);
  const targetBeaten = cumulative >= target;
  return (
    <div data-testid="score-bar" className="rounded-lg border border-stone-200 bg-white px-4 py-2">
      <div className="flex items-baseline justify-between text-sm">
        <p>
          <span className="text-2xl font-bold tabular-nums">{cumulative}</span>
          <span className="text-stone-500"> / {target} target</span>
        </p>
        <p className="text-stone-600">
          Move {Math.min(movesUsed + 1, moveCount)} of {moveCount}
        </p>
      </div>

      {targetBeaten ? (
        <p className="mt-0.5 text-xs font-semibold text-emerald-700">
          Target beaten — keep playing for a higher score, or bank it.
        </p>
      ) : null}

      <div aria-live="polite" className="mt-1 min-h-6 text-sm">
        {!hasPreview ? (
          <span> </span>
        ) : preview.message ? (
          <span className="text-red-600">{preview.message}</span>
        ) : (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {preview.words.map((w) => (
              <span
                key={w.word + w.cells.map((c) => `${c.row},${c.col}`).join('|')}
                data-testid={`word-check-${w.word}`}
                className={`rounded px-1.5 py-0.5 font-medium ${
                  w.valid ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'
                }`}
              >
                {w.word} {w.valid ? '✓' : '✗'} {w.score}
              </span>
            ))}
            {preview.valid ? (
              <span className="font-semibold text-emerald-700">
                +{preview.score}
                {preview.bingo ? ' (bingo +50 included)' : ''} → {cumulative + preview.score}/
                {target}
              </span>
            ) : (
              <span className="text-red-600">
                {preview.words.filter((w) => !w.valid).length === 1
                  ? 'not a word'
                  : 'not valid words'}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
